// ── Listing Intelligence data types and placeholder data ──

export type SeverityLevel = "critical" | "warning" | "suggestion"
export type ScoreLabel = "Good" | "Needs Work" | "Poor" | "Critical"
export type LetterGrade = "A" | "B" | "C" | "D"
export type AlignmentStatus = "confirmed" | "unconfirmed" | "missed"

export interface DimensionScore {
  key: string
  label: string
  score: number
  max: number
  scoreLabel: ScoreLabel
  description: string
}

export interface AuditIssue {
  id: string
  severity: SeverityLevel
  category: string
  title: string
  description: string
  source: string
  fixable: boolean
  fixType?: "title" | "description" | "amenities" | "seasonal"
}

export interface AlignmentRow {
  claim: string
  evidence: string
  status: AlignmentStatus
}

export interface SeasonalMismatch {
  text: string
}

export interface ListingTab {
  id: string
  name: string
  grade: LetterGrade
}

export interface ListingAuditData {
  // Meta
  hasAudit: boolean
  isLoading: boolean
  isPaidPlan: boolean
  listings: ListingTab[]
  activeListingId: string

  // Hero
  compositeScore: number
  grade: LetterGrade
  scoreDelta: number | null
  lastAuditedAt: string | null

  // Score breakdown
  dimensions: DimensionScore[]

  // Content audit issues
  issues: AuditIssue[]

  // Review alignment
  hasReviews: boolean
  alignment: AlignmentRow[]

  // Seasonal
  hasSeasonProfiles: boolean
  currentSeason: string
  seasonalEmphasis: string
  upcomingSeason: { name: string; date: string; emphasis: string } | null
  seasonalMismatches: SeasonalMismatch[]
}

export const PLACEHOLDER_LISTING_DATA: ListingAuditData = {
  hasAudit: true,
  isLoading: false,
  isPaidPlan: true,
  listings: [{ id: "1", name: "Airbnb", grade: "B" }],
  activeListingId: "1",

  compositeScore: 68,
  grade: "B",
  scoreDelta: 4,
  lastAuditedAt: "2 days ago",

  dimensions: [
    {
      key: "content",
      label: "Content Quality",
      score: 72,
      max: 100,
      scoreLabel: "Needs Work",
      description:
        "Title is 18 characters \u2014 top listings in your area average 45. Description structure is adequate but lacks a compelling opening hook.",
    },
    {
      key: "amenities",
      label: "Amenity Completeness",
      score: 58,
      max: 100,
      scoreLabel: "Poor",
      description:
        "3 amenities found in your house rules aren\u2019t listed: coffee machine, BBQ, and bicycle storage. Adding them would close the gap.",
    },
    {
      key: "reviews",
      label: "Review Alignment",
      score: 81,
      max: 100,
      scoreLabel: "Good",
      description:
        "Guest reviews broadly confirm what your listing promises. Two missed opportunities detected where guests praise things not mentioned.",
    },
    {
      key: "seasonal",
      label: "Seasonal Relevance",
      score: 45,
      max: 100,
      scoreLabel: "Poor",
      description:
        "Your description references \u2018beach days and swimming\u2019 but winter has been active since December. Content should shift to indoor comfort and off-season activities.",
    },
    {
      key: "photos",
      label: "Photo Assessment",
      score: 0,
      max: 100,
      scoreLabel: "Critical",
      description:
        "No photo data available. When OTA photo analysis is enabled, this will assess count, quality, and cover photo relevance.",
    },
  ],

  issues: [
    {
      id: "i1",
      severity: "critical",
      category: "Content",
      title: "Title too short",
      description:
        "Your title is 18 characters. Top-performing listings in your area average 45 characters with location keywords. A longer, keyword-rich title improves search visibility.",
      source: "Content Analysis",
      fixable: true,
      fixType: "title",
    },
    {
      id: "i2",
      severity: "critical",
      category: "Seasonal",
      title: "Off-season content mismatch",
      description:
        "Description mentions \u2018beach access\u2019 and \u2018swimming\u2019 but winter season has been active since December. This creates expectation gaps for guests booking now.",
      source: "Season Profile",
      fixable: true,
      fixType: "seasonal",
    },
    {
      id: "i3",
      severity: "warning",
      category: "Amenities",
      title: "Missing common amenities",
      description:
        "Your house rules mention a coffee machine, BBQ on the terrace, and bicycle storage \u2014 none of these appear in your listing amenities. Guests searching for these features won\u2019t find your property.",
      source: "House Rules",
      fixable: true,
      fixType: "amenities",
    },
    {
      id: "i4",
      severity: "warning",
      category: "Content",
      title: "Description lacks structure",
      description:
        "Your description is a single block of text. Breaking it into sections (The Space, The Neighbourhood, Getting Around) improves readability and scannability.",
      source: "Content Analysis",
      fixable: true,
      fixType: "description",
    },
    {
      id: "i5",
      severity: "suggestion",
      category: "Reviews",
      title: "Guest-praised feature not mentioned",
      description:
        "Multiple guests mention \u201Cincredible mountain views\u201D but your description doesn\u2019t reference views at all. This is free value left on the table.",
      source: "Reviews",
      fixable: true,
      fixType: "description",
    },
    {
      id: "i6",
      severity: "suggestion",
      category: "Reviews",
      title: "Walkability not highlighted",
      description:
        "Guests frequently praise \u201Cwalking distance to restaurants and shops\u201D but this isn\u2019t in your listing. Consider adding neighbourhood walkability as a selling point.",
      source: "Reviews",
      fixable: true,
      fixType: "description",
    },
  ],

  hasReviews: true,
  alignment: [
    {
      claim: "Stunning mountain views",
      evidence: "\u201CThe views from the balcony were absolutely breathtaking\u201D \u2014 6 reviews",
      status: "confirmed",
    },
    {
      claim: "Walking distance to restaurants",
      evidence: "\u201CEverything was walkable, great location for dining\u201D \u2014 4 reviews",
      status: "confirmed",
    },
    {
      claim: "Fully equipped kitchen",
      evidence: "\u201CThe stove wasn\u2019t working properly, nor the dishwasher\u201D \u2014 3 reviews",
      status: "unconfirmed",
    },
    {
      claim: "Quiet retreat",
      evidence: "No guest has mentioned noise levels positively or negatively",
      status: "unconfirmed",
    },
    {
      claim: "Outdoor BBQ area",
      evidence: "\u201CThe outdoor BBQ area was a highlight of our stay\u201D \u2014 5 reviews",
      status: "missed",
    },
    {
      claim: "Morning beach runs",
      evidence: "\u201CPerfect for morning runs along the beach path\u201D \u2014 3 reviews",
      status: "missed",
    },
  ],

  hasSeasonProfiles: true,
  currentSeason: "Winter",
  seasonalEmphasis:
    "Highlight fireside comfort, indoor activities, snow sports nearby, and cosy evening ambiance.",
  upcomingSeason: {
    name: "Spring",
    date: "March 20",
    emphasis:
      "Shift to hiking trails, wildflowers, outdoor dining, and longer daylight hours.",
  },
  seasonalMismatches: [
    {
      text: "Your description mentions \u2018beach access\u2019 but winter season has been active since December.",
    },
    {
      text: "No reference to indoor activities or winter-specific amenities like fireplace or heated floors.",
    },
  ],
}

// Content generator mock variants
export interface GeneratedVariant {
  id: string
  text: string
  rationale: string
  sources: string[]
  charCount: number
}

export const MOCK_TITLE_VARIANTS: GeneratedVariant[] = [
  {
    id: "t1",
    text: "Mountain-View Beach Apartment \u00B7 Falsterbo, Steps to Sea",
    rationale:
      "Leads with the mountain location keyword and proximity to beach \u2014 the two most-praised features in reviews.",
    sources: ["Reviews", "Guide Places"],
    charCount: 54,
  },
  {
    id: "t2",
    text: "Sunlit Falsterbo Retreat \u00B7 Sea Views & Garden BBQ",
    rationale:
      "Highlights the BBQ (mentioned in 5 reviews) and natural light, which guests associate with the property.",
    sources: ["Reviews", "House Rules"],
    charCount: 49,
  },
  {
    id: "t3",
    text: "Coastal Falsterbo Home \u00B7 Walk to Restaurants & Beach",
    rationale:
      "Emphasises walkability, the #2 strength in review sentiment, combined with coastal positioning.",
    sources: ["Reviews", "Guide Places"],
    charCount: 52,
  },
  {
    id: "t4",
    text: "Charming Falsterbo Apartment with Panoramic Views",
    rationale:
      "Classic structure that leads with charm and the view \u2014 strong for Airbnb search where \u2018charming\u2019 converts well.",
    sources: ["Reviews"],
    charCount: 49,
  },
  {
    id: "t5",
    text: "Newly Refurbished Falsterbo Flat \u00B7 Beach & Mountain Views",
    rationale:
      "Leads with \u2018newly refurbished\u2019 (mentioned in recent reviews) for freshness signal, combines two location anchors.",
    sources: ["Reviews", "Season Profile"],
    charCount: 56,
  },
]

export const MOCK_DESCRIPTION_VARIANTS: GeneratedVariant[] = [
  {
    id: "d1",
    text: "Wake up to panoramic mountain views from your private balcony in this newly refurbished Falsterbo apartment. Steps from the beach and within walking distance of the village\u2019s best restaurants, this is a base for both coastal relaxation and active exploration.\n\nThe Space\nA bright, airy apartment with a fully equipped kitchen, comfortable living area, and a terrace with BBQ \u2014 perfect for long summer evenings or cosy winter meals.\n\nThe Neighbourhood\nFalsterbo offers quiet beach walks, cycling paths, and a charming village centre. Restaurants, caf\u00E9s, and grocery stores are all within a 5-minute walk.",
    rationale:
      "Structured into scannable sections, leads with the strongest review-confirmed feature (views), and incorporates walkability and BBQ \u2014 both highly praised by guests.",
    sources: ["Reviews", "Guide Places", "House Rules"],
    charCount: 612,
  },
  {
    id: "d2",
    text: "A bright coastal apartment in Falsterbo where every morning starts with mountain views and every evening ends with a BBQ on the terrace. Recently refurbished with attention to comfort, this home puts you steps from the beach and walking distance from everything the village offers.\n\nWhat makes it special: guests consistently highlight the views, the walkability, and the peaceful setting. Whether you\u2019re here for a beach holiday or a quiet winter escape, the apartment adapts to the season.\n\nPractical details: fully equipped kitchen, bicycle storage, fast WiFi, and detailed local guides available on arrival.",
    rationale:
      "Uses guest language directly (\u2018peaceful setting\u2019, \u2018walkability\u2019) to create authenticity. Mentions seasonal adaptability to address the current winter relevance gap.",
    sources: ["Reviews", "Season Profile", "House Rules"],
    charCount: 589,
  },
  {
    id: "d3",
    text: "Set in the heart of Falsterbo with views stretching from the coast to the mountains, this refurbished apartment is built for guests who want to explore and unwind in equal measure.\n\nYou\u2019ll find everything you need for a comfortable stay \u2014 a well-equipped kitchen, a terrace with BBQ for outdoor cooking, and bicycle storage for exploring the coast. The beach is a short walk away, and the village\u2019s restaurants and shops are even closer.\n\nLocal tip: the coastal path is perfect for morning runs (a favourite among previous guests), and the area transforms beautifully with each season.",
    rationale:
      "Incorporates the \u2018morning runs\u2019 insight from reviews (missed opportunity), positions the property for all-season relevance, and uses a \u2018local tip\u2019 format that performs well on Airbnb.",
    sources: ["Reviews", "Guide Places", "Season Profile"],
    charCount: 601,
  },
]
